import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dropdown, Menu, Badge, Tooltip,
} from 'antd';
import {
  CloudDownloadOutlined,
  EditOutlined,
  TagOutlined,
  FolderOpenOutlined,
  MergeCellsOutlined,
  BellOutlined,
  MessageOutlined,
  PhoneOutlined,
  SendOutlined,
  SwapOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {
  ClipboardText,
  FolderSimplePlus,
  ListChecks,
  Desktop,
  CaretDown,
  DotsThree,
  UserList,
  CloudArrowUp,
} from '@phosphor-icons/react';
import Button from '../../../../components/Button';
import { trackReferralActionClicked } from '../../../../global/analytics';
import { captureAnalytics } from '../../../../actions/preload';
import {
  NO_TEAM_VALUE,
  teamChip,
  allSelectedTeamEditable,
  teamReassignNeedsConfirm,
  confirmTeamReassign,
} from '../../../../components/teams/teamPicker';
import {
  assignActionLabel,
  chartClarityTooltips,
  hasFeatureInLocalStorage,
  INBOX_ACTION_CLARITY_FEATURE,
} from '../../../../global/featureFlags';
import { getArchiveSoftBlockStatuses } from './helpers/archiveSoftBlock';
import PendingApprovalsDrawer from './PendingApprovalsDrawer';
import usePendingApprovals from './usePendingApprovals';
import { deriveReferralActionState, deriveAssignmentTargets } from './referralActionState';
import { shouldUseLegacyEmrActions } from '../../../../components/chartActions/credentialCapabilities';

const ReferralActionBar = ({
  selectedData,
  data: allDataOnPage,
  actions,
  flags,
  hasEmrIntegrationCredential,
  credentialSupportsStandaloneTask,
  autoUploadEmr,
  title,
  downloadInPrimary,
  allowReplyChartActions,
  useReferralArchiveWarnings,
  teamsEnabled,
  teams,
  onSetTeamBulk,
}) => {
  const {
    downloadRecord,
    editTag,
    performAction,
    archiveRecordOpen,
    unarchiveRecordOpen,
    autoUploadRecord,
    newChart,
    createTask,
    notifyModal,
    transferToDepartmentModal,
    editPatientOpen,
    smsMessage,
    mairaCall,
    referralReply,
    createAssignedDocument,
    handleMergeRecords,
    handleMergeAndArchiveRecords,
    handleDropdownSendToChart,
    handleDropdownCreateChart,
    handleDropdownCreateTask,
    isEmailRecord,
    uploadToSFTPFolder,
  } = actions;

  const {
    canForward,
    autoUploadToEMR,
    createPatientChart,
    sendSmsMessage,
    sendMairaCall,
    referralReplyTemplates,
    hasReferralPortal,
    uploadToSFTPFolder: hasSftpUpload,
  } = flags;

  const [approvalsDrawerVisible, setApprovalsDrawerVisible] = useState(false);
  const {
    partners: pendingPartners,
    totalCount: pendingCount,
    loading: pendingLoading,
    actionLoading: pendingActionLoading,
    approvePartner,
    rejectPartner,
  } = usePendingApprovals({ enabled: hasReferralPortal });

  // Shared with RowQuickActions (the per-row hover menu) so toolbar and row
  // action gating can never drift.
  const {
    selectedCount,
    hasSelection,
    selectedAll,
    selectedRecord,
    replySelected,
    downloadDenied,
    labelDenied,
    notifyDenied,
    assignDenied,
    sftpDenied,
    archiveDenied,
    patientSplitRestricted,
    viewingArchivedRecords,
    uploadDisabled,
    chartReplyDisabled,
    dropdownDisabled,
    disableChartActions,
    canEdit,
    canStandaloneTask,
  } = deriveReferralActionState({
    selectedData,
    allDataOnPage,
    flags,
    actions,
    hasEmrIntegrationCredential,
    autoUploadEmr,
    allowReplyChartActions,
    credentialSupportsStandaloneTask,
  });
  const clarityTooltips = chartClarityTooltips(autoUploadEmr);
  // ENG-3339: with inbox_action_clarity off, Notify still carries the department
  // chips and this second button does not exist.
  const actionClarityEnabled = hasFeatureInLocalStorage(INBOX_ACTION_CLARITY_FEATURE);
  const useLegacyEmrActions = shouldUseLegacyEmrActions({
    hasEmrIntegrationCredential,
    autoUploadEmr,
  });

  const selectionLabel = selectedAll
    ? `${allDataOnPage.length} (all)`
    : `${selectedCount}`;

  // Bulk Team selector (D4). Operates on the same rows the other bulk actions do
  // (all rows on the page when Select-All is active, otherwise the selected rows).
  const teamTargetRecords = selectedAll
    ? allDataOnPage
    : selectedData.filter((record) => record && record !== 'all');
  const teamBulkCount = teamTargetRecords.length;
  // Enabled only when EVERY selected record is eligible; grayed out otherwise.
  const teamBulkEnabled = allSelectedTeamEditable(teamTargetRecords);

  const handleBulkTeamChange = async (value) => {
    const nextSlug = value === NO_TEAM_VALUE ? null : value;
    // Respect the reassign confirm if ANY selected record would lose visibility
    // by moving to a different team; one confirm covers the whole batch.
    const warningRecord = teamTargetRecords.find(
      (record) => teamReassignNeedsConfirm({ record, nextTeamSlug: nextSlug }),
    );
    if (warningRecord) {
      const confirmed = await confirmTeamReassign({ record: warningRecord, nextTeamSlug: nextSlug });
      if (!confirmed) return;
    }
    captureAnalytics('record_team_bulk_set', { count: teamBulkCount, cleared: nextSlug === null });
    onSetTeamBulk(
      teamTargetRecords.map((record) => ({ id: record.send_token, receiver_type: record.receiver_type })),
      nextSlug,
    );
  };

  const teamBulkMenu = (
    <Menu onClick={({ key }) => handleBulkTeamChange(key)}>
      <Menu.Item key={NO_TEAM_VALUE}>No team</Menu.Item>
      {teams.map((team) => (
        <Menu.Item key={team.id}>{teamChip(team)}</Menu.Item>
      ))}
    </Menu>
  );

  // Same derivation createAssignedDocument builds its payload from, so the action
  // is offered exactly when every row it would send can be assigned (ENG-3409).
  const {
    assignable: assignTargetsAssignable,
    overBatchLimit: assignOverBatchLimit,
  } = deriveAssignmentTargets({ selectedData, allDataOnPage, selectedAll });
  const assignDisabled = !hasSelection
    || assignDenied
    || !assignTargetsAssignable
    || assignOverBatchLimit;

  const trackActionClick = (action, placement = 'primary') => {
    trackReferralActionClicked({
      action,
      placement,
      selectedCount,
      selectedAll,
      isArchivedView: viewingArchivedRecords,
    });
  };

  const emrActionsMenu = (
    <Menu>
      {autoUploadToEMR && (
        <Menu.Item
          key="sendToChartEhr"
          disabled={disableChartActions}
          onClick={() => {
            trackActionClick('send_to_chart', 'chart_dropdown');
            if (selectedRecord) handleDropdownSendToChart(selectedRecord);
          }}
        >
          <Tooltip title={clarityTooltips.sendToChart || null} placement="bottom">
            <span>Send to Chart</span>
          </Tooltip>
        </Menu.Item>
      )}
      {createPatientChart && (
        <Menu.Item
          key="createChartEhr"
          disabled={disableChartActions}
          onClick={() => {
            trackActionClick('create_new_chart', 'chart_dropdown');
            if (selectedRecord) handleDropdownCreateChart(selectedRecord);
          }}
        >
          Create New Chart
        </Menu.Item>
      )}
      {canStandaloneTask && (
        <Menu.Item
          key="createTaskEhr"
          disabled={disableChartActions}
          onClick={() => {
            trackActionClick(autoUploadEmr === 'Kareo' ? 'create_note' : 'create_task', 'chart_dropdown');
            if (selectedRecord) handleDropdownCreateTask(selectedRecord);
          }}
        >
          {autoUploadEmr === 'Kareo' ? 'Create Note' : 'Create Task'}
        </Menu.Item>
      )}
    </Menu>
  );

  // Overflow menu - lower-usage actions
  const overflowMenu = (
    <Menu className="referral-overflow-menu">
      {!downloadInPrimary && (
        <Menu.Item
          key="download"
          icon={<CloudDownloadOutlined />}
          disabled={!hasSelection || downloadDenied}
          onClick={() => {
            trackActionClick('download', 'overflow');
            downloadRecord();
          }}
        >
          Download
        </Menu.Item>
      )}
      {canForward && !autoUploadToEMR && (
        <Menu.Item
          key="forwardEmr"
          icon={<Desktop size={16} />}
          disabled={selectedCount !== 1 || selectedAll || replySelected || patientSplitRestricted}
          onClick={() => {
            trackActionClick('forward_to_emr', 'overflow');
            if (selectedRecord) performAction(selectedRecord.send_token, 'FORWARD_RECORD');
          }}
        >
          Forward to EMR
        </Menu.Item>
      )}
      <Menu.Item
        key="assignDoc"
        data-tutorial="assign-action"
        icon={<BellOutlined />}
        disabled={assignDisabled}
        onClick={() => {
          trackActionClick('assign_document', 'overflow');
          createAssignedDocument();
        }}
      >
        {assignActionLabel()}
      </Menu.Item>
      {hasSftpUpload && (
        <Menu.Item
          key="sftpUpload"
          icon={<CloudArrowUp size={16} />}
          disabled={selectedCount !== 1 || sftpDenied || !selectedRecord || selectedRecord.receiver_type !== 'received_fax'}
          onClick={() => {
            trackActionClick('upload_to_sftp', 'overflow');
            if (selectedRecord) uploadToSFTPFolder(selectedRecord.send_token);
          }}
        >
          Upload to SFTP Folder
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item
        key="merge"
        icon={<MergeCellsOutlined />}
        disabled={selectedCount <= 1 || patientSplitRestricted}
        onClick={() => {
          trackActionClick('merge_records', 'overflow');
          handleMergeRecords(selectedData);
        }}
      >
        Merge Records
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="referral-action-bar">
      <h2 className="referral-action-bar__title">{title}</h2>
      {hasSelection && (
        <span className="referral-action-bar__count">
          {selectionLabel}
          {' selected'}
        </span>
      )}
      {hasSelection && <div className="referral-action-bar__divider" />}

      <div className="referral-action-bar__actions">
        {/* PRIMARY: Edit (50k clicks) */}
        <Button
          type="secondary"
          size="xsmall"
          label="Edit"
          leftIcon={<EditOutlined />}
          disabled={canEdit || selectedCount !== 1}
          onClick={() => {
            trackActionClick('edit');
            editPatientOpen();
          }}
        />

        {/* PRIMARY: Chart actions (22k combined) */}
        {autoUploadToEMR && useLegacyEmrActions && (
          <Tooltip title={clarityTooltips.sendToChart || null} placement="bottom">
            <div>
              <Button
                type="secondary"
                size="xsmall"
                label="Send to Chart"
                leftIcon={<ClipboardText size={16} weight="fill" />}
                disabled={selectedCount !== 1 || chartReplyDisabled || uploadDisabled}
                onClick={() => {
                  trackActionClick('send_to_chart');
                  if (selectedRecord) autoUploadRecord(selectedRecord);
                }}
              />
            </div>
          </Tooltip>
        )}
        {createPatientChart && useLegacyEmrActions && (
          <Button
            type="secondary"
            size="xsmall"
            label="Create Chart"
            leftIcon={<FolderSimplePlus size={16} />}
            disabled={selectedCount !== 1 || chartReplyDisabled || uploadDisabled}
            onClick={() => {
              trackActionClick('create_chart');
              newChart();
            }}
          />
        )}
        {hasEmrIntegrationCredential && (autoUploadToEMR || createPatientChart || canStandaloneTask) && (
          <Dropdown overlay={emrActionsMenu} trigger={['click']}>
            <div>
              <Button
                type="secondary"
                size="xsmall"
                label="Chart Actions"
                leftIcon={<ClipboardText size={16} weight="fill" />}
                rightIcon={<CaretDown size={10} />}
                data-tutorial="chart-action"
                disabled={dropdownDisabled}
                onClick={() => trackActionClick('chart_actions')}
              />
            </div>
          </Dropdown>
        )}

        {/* PRIMARY: Archive (10k clicks) */}
        <Button
          type="secondary"
          size="xsmall"
          label={viewingArchivedRecords ? 'Unarchive' : 'Archive'}
          leftIcon={<FolderOpenOutlined />}
          data-tutorial="archive-action"
          disabled={selectedCount === 0 || archiveDenied}
          onClick={() => {
            if (archiveDenied) return;
            trackActionClick(viewingArchivedRecords ? 'unarchive' : 'archive');
            const selectedRecords = selectedAll ? allDataOnPage : selectedData;
            const tokens = selectedRecords.map((x) => x.send_token);
            if (viewingArchivedRecords) {
              unarchiveRecordOpen(tokens);
            } else {
              const softBlockStatuses = useReferralArchiveWarnings
                ? getArchiveSoftBlockStatuses(selectedRecords)
                : [];
              archiveRecordOpen(tokens, softBlockStatuses);
            }
          }}
        />

        {/* PRIMARY: Labels (7.3k clicks) */}
        <Button
          type="secondary"
          size="xsmall"
          label={selectedCount > 1 ? 'Labels' : 'Label'}
          leftIcon={<TagOutlined />}
          data-tutorial="label-action"
          disabled={selectedCount === 0 || labelDenied}
          onClick={() => {
            trackActionClick(selectedCount > 1 ? 'labels' : 'label');
            editTag();
          }}
        />

        {/* PRIMARY: Team (D4) — same single-select team picker as the inline cell,
            with a live count. Disabled when any selected record can't be reassigned. */}
        {teamsEnabled && hasSelection && (
          <Tooltip
            title={teamBulkEnabled ? null : 'Some selected records can\'t be reassigned'}
            placement="bottom"
          >
            <span>
              <Dropdown
                overlay={teamBulkMenu}
                trigger={['click']}
                disabled={!teamBulkEnabled}
              >
                <div>
                  <Button
                    type="secondary"
                    size="xsmall"
                    label={`Set team (${teamBulkCount})`}
                    leftIcon={<TeamOutlined />}
                    disabled={!teamBulkEnabled}
                    onClick={() => trackActionClick('set_team')}
                  />
                </div>
              </Dropdown>
            </span>
          </Tooltip>
        )}

        {/* PRIMARY: Send SMS (2.3k clicks) — Referrals-only; gated so Inbox redesign doesn't surface it. */}
        {sendSmsMessage && (
          <Button
            type="secondary"
            size="xsmall"
            label="SMS"
            leftIcon={<MessageOutlined />}
            disabled={selectedCount !== 1 || replySelected || uploadDisabled}
            onClick={() => {
              trackActionClick('sms');
              if (selectedRecord) smsMessage(selectedRecord, sendSmsMessage);
            }}
          />
        )}

        {sendMairaCall && (
          <Button
            type="secondary"
            size="xsmall"
            label="MAIRA Call"
            leftIcon={<PhoneOutlined />}
            disabled={selectedCount !== 1 || replySelected || uploadDisabled}
            onClick={() => {
              trackActionClick('maira_call');
              if (selectedRecord) mairaCall(selectedRecord, sendMairaCall);
            }}
          />
        )}
        <Button
          type="secondary"
          size="xsmall"
          label="Notify"
          leftIcon={<BellOutlined />}
          disabled={selectedCount < 1 || notifyDenied}
          onClick={() => {
            trackActionClick('notify');
            notifyModal();
          }}
        />

        {actionClarityEnabled && (
          <Button
            type="secondary"
            size="xsmall"
            label="Transfer to department"
            leftIcon={<SwapOutlined />}
            disabled={selectedCount < 1 || replySelected}
            onClick={() => {
              trackActionClick('transfer_to_department');
              transferToDepartmentModal();
            }}
          />
        )}

        {downloadInPrimary && (
          <Button
            type="secondary"
            size="xsmall"
            label="Download"
            leftIcon={<CloudDownloadOutlined />}
            disabled={!hasSelection || downloadDenied}
            onClick={() => {
              trackActionClick('download');
              downloadRecord();
            }}
          />
        )}

        {referralReplyTemplates && referralReplyTemplates.length > 0 && (
          <Button
            type="secondary"
            size="xsmall"
            label="Referral Replies"
            leftIcon={<SendOutlined />}
            disabled={selectedCount !== 1 || replySelected || isEmailRecord(selectedData) || uploadDisabled}
            onClick={() => {
              trackActionClick('referral_replies');
              if (selectedRecord) referralReply(selectedRecord);
            }}
          />
        )}

        <Button
          type="secondary"
          size="xsmall"
          label="Merge & Archive"
          leftIcon={<MergeCellsOutlined />}
          disabled={selectedCount <= 1 || patientSplitRestricted}
          onClick={() => {
            trackActionClick('merge_and_archive');
            handleMergeAndArchiveRecords(selectedData);
          }}
        />

        {canStandaloneTask && useLegacyEmrActions && (
          <Tooltip title={clarityTooltips.createTask || null} placement="bottom">
            <div>
              <Button
                type="secondary"
                size="xsmall"
                label={autoUploadEmr === 'Kareo' ? 'Create Note' : 'Create Task'}
                leftIcon={<ListChecks size={16} />}
                disabled={selectedCount !== 1 || replySelected || uploadDisabled}
                onClick={() => {
                  trackActionClick(autoUploadEmr === 'Kareo' ? 'create_note' : 'create_task');
                  createTask();
                }}
              />
            </div>
          </Tooltip>
        )}

        {/* Pending partner approvals */}
        {hasReferralPortal && pendingCount > 0 && (
          <Badge count={pendingCount} size="small" offset={[-8, 4]}>
            <Button
              type="secondary"
              size="xsmall"
              label="Partner Approvals"
              leftIcon={<UserList size={16} />}
              onClick={() => {
                trackActionClick('pending_approvals');
                setApprovalsDrawerVisible(true);
              }}
            />
          </Badge>
        )}

        {/* OVERFLOW: Everything else */}
        <Dropdown
          overlay={overflowMenu}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            type="button"
            className="referral-action-bar__overflow-btn"
            data-tutorial="more-actions"
            onClick={() => trackActionClick('overflow_menu_open', 'overflow')}
          >
            <DotsThree size={20} weight="bold" />
          </button>
        </Dropdown>
      </div>
      <PendingApprovalsDrawer
        visible={approvalsDrawerVisible}
        onClose={() => setApprovalsDrawerVisible(false)}
        partners={pendingPartners}
        loading={pendingLoading}
        actionLoading={pendingActionLoading}
        onApprove={approvePartner}
        onReject={rejectPartner}
      />
    </div>
  );
};

ReferralActionBar.defaultProps = {
  selectedData: [],
  credentialSupportsStandaloneTask: false,
  autoUploadEmr: '',
  title: 'Referral Management',
  downloadInPrimary: false,
  allowReplyChartActions: false,
  useReferralArchiveWarnings: true,
  teamsEnabled: false,
  teams: [],
  onSetTeamBulk: () => {},
};

ReferralActionBar.propTypes = {
  selectedData: PropTypes.array,
  data: PropTypes.array.isRequired,
  actions: PropTypes.object.isRequired,
  flags: PropTypes.object.isRequired,
  hasEmrIntegrationCredential: PropTypes.bool.isRequired,
  credentialSupportsStandaloneTask: PropTypes.bool,
  autoUploadEmr: PropTypes.string,
  title: PropTypes.string,
  downloadInPrimary: PropTypes.bool,
  allowReplyChartActions: PropTypes.bool,
  useReferralArchiveWarnings: PropTypes.bool,
  teamsEnabled: PropTypes.bool,
  teams: PropTypes.array,
  onSetTeamBulk: PropTypes.func,
};

export default ReferralActionBar;
